/**
 * Forensic Flags Query Dialog
 * Allows users to query forensic flags for selected devices
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  Paper,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import geoneutronApi, { ForensicFlagData } from '../../services/geoneutronApi';

interface ForensicFlagsDialogProps {
  open: boolean;
  onClose: () => void;
  devices: Array<{
    registration_id: string;
    latitude?: number;
    longitude?: number;
    timestamp?: string;
  }>;
  dateRange: {
    start: Date;
    end: Date;
  };
  activeTabLabel?: string;
  onFlagsLoaded: (flagsData: Record<string, ForensicFlagData>) => void;
}

const ForensicFlagsDialog: React.FC<ForensicFlagsDialogProps> = ({
  open,
  onClose,
  devices,
  dateRange,
  activeTabLabel = 'Current Tab',
  onFlagsLoaded,
}) => {
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [queryResults, setQueryResults] = useState<Record<string, ForensicFlagData> | null>(null);

  // Get unique device registration IDs
  const uniqueDevices = useMemo(() => {
    const deviceMap = new Map<string, any>();
    devices.forEach(device => {
      if (device.registration_id && !deviceMap.has(device.registration_id)) {
        deviceMap.set(device.registration_id, device);
      }
    });
    return Array.from(deviceMap.values());
  }, [devices]);

  // Track if dialog was just opened (to reset state only on open, not on prop changes)
  const [wasOpen, setWasOpen] = React.useState(false);
  
  // Select all devices by default when dialog FIRST opens (not on prop changes while open)
  React.useEffect(() => {
    if (open && !wasOpen) {
      // Dialog just opened - reset everything
      setWasOpen(true);
      setSelectedDevices(new Set(uniqueDevices.map(d => d.registration_id)));
      setError(null);
      setSuccess(false);
      setQueryResults(null);
      setProgress(0);
    } else if (!open && wasOpen) {
      // Dialog closed - reset flag
      setWasOpen(false);
    }
  }, [open, wasOpen, uniqueDevices]);

  const handleToggleDevice = (regId: string) => {
    setSelectedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(regId)) {
        newSet.delete(regId);
      } else {
        newSet.add(regId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedDevices(new Set(uniqueDevices.map(d => d.registration_id)));
  };

  const handleSelectNone = () => {
    setSelectedDevices(new Set());
  };

  const handleQueryFlags = async () => {
    if (selectedDevices.size === 0) {
      setError('Please select at least one device to query');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setProgress(0);

    try {
      const registrationIds = Array.from(selectedDevices);
      const startDateISO = dateRange.start.toISOString();
      const endDateISO = dateRange.end.toISOString();

      
      // Simulate progress (actual query might take a while)
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const response = await geoneutronApi.queryForensicFlags(
        registrationIds,
        startDateISO,
        endDateISO
      );

      clearInterval(progressInterval);
      setProgress(100);


      // Store results for display (ensure it's an object)
      const devicesData = response.devices || {};
      setQueryResults(devicesData);
      
      // Pass the flags data back to parent
      onFlagsLoaded(devicesData);

      setSuccess(true);
      // Don't auto-close - let user see the results

    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to query forensic flags');
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="secondary" />
          <Typography variant="h6" component="span">
            Query Forensic Flags
          </Typography>
          <Chip 
            label={activeTabLabel} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ ml: 1 }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Forensic flags</strong> provide metadata about signal quality and potential anomalies.
            Select devices to query for flags like <strong>SPOOF_LOCATION</strong>, <strong>LIKELY_DRIVING</strong>, 
            <strong>CELL_TOWER_DERIVED</strong>, and more.
          </Typography>
        </Alert>

        {/* Date Range Info */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.default' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            <strong>Query Period:</strong>
          </Typography>
          <Typography variant="body2">
            {dateRange.start.toLocaleDateString()} - {dateRange.end.toLocaleDateString()}
            {' '}
            <Chip 
              label={`${Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24))} days`}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
        </Paper>

        {/* Device Selection */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Select Devices ({selectedDevices.size} of {uniqueDevices.length})
            </Typography>
            <Box>
              <Button size="small" onClick={handleSelectAll}>Select All</Button>
              <Button size="small" onClick={handleSelectNone}>Select None</Button>
            </Box>
          </Box>

          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
            <List dense>
              {uniqueDevices.map((device, index) => (
                <React.Fragment key={device.registration_id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    button
                    onClick={() => handleToggleDevice(device.registration_id)}
                    sx={{ 
                      bgcolor: selectedDevices.has(device.registration_id) ? 'action.selected' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedDevices.has(device.registration_id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="bold">
                          {device.registration_id.slice(0, 13)}...
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {device.timestamp ? new Date(device.timestamp).toLocaleDateString() : 'Unknown date'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>

        {/* Common Forensic Flags Reference */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Common Forensic Flags:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip label="SPOOF_LOCATION" size="small" color="error" />
            <Chip label="LIKELY_DRIVING" size="small" color="warning" />
            <Chip label="CELL_TOWER_DERIVED" size="small" color="info" />
            <Chip label="GPS_LOCATION" size="small" color="success" />
            <Chip label="WIFI_LOCATION" size="small" color="default" />
            <Chip label="HIGH_ACCURACY" size="small" color="success" />
            <Chip label="LAT_GRID_LOCATION" size="small" color="warning" />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            These flags will be automatically detected and displayed for queried devices.
          </Typography>
        </Box>

        {/* Loading Progress */}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Querying forensic flags... ({progress}%)
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {/* Success Message with Results Summary */}
        {success && queryResults && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              ✅ Forensic flags loaded successfully!
            </Typography>
          </Alert>
        )}
        
        {/* Results Summary */}
        {queryResults && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              📊 Results Summary ({Object.keys(queryResults).length} devices)
            </Typography>
            
            {Object.keys(queryResults).length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                No forensic flags found for the selected devices in this time range.
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {Object.entries(queryResults).map(([regId, data]) => (
                  <Box 
                    key={regId} 
                    sx={{ 
                      p: 1, 
                      mb: 1, 
                      bgcolor: 'background.paper', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {regId.slice(0, 13)}...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.total_signals || 0} signals • {data.signals_with_flags || 0} with flags 
                      {data.total_signals > 0 && ` (${Math.round(((data.signals_with_flags || 0) / data.total_signals) * 100)}%)`}
                    </Typography>
                    {data.unique_flags && data.unique_flags.length > 0 ? (
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {data.unique_flags.slice(0, 5).map(flag => (
                          <Chip
                            key={flag}
                            label={`${flag} (${data.flag_counts?.[flag] || 0})`}
                            size="small"
                            color={
                              flag.includes('SPOOF') ? 'error' :
                              flag.includes('LIKELY') ? 'warning' :
                              flag.includes('CELL') ? 'info' : 'default'
                            }
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        ))}
                        {data.unique_flags.length > 5 && (
                          <Chip 
                            label={`+${data.unique_flags.length - 5} more`} 
                            size="small" 
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        No flags detected for this device
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" icon={<ErrorIcon />}>
            <strong>Query Failed:</strong> {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {queryResults ? 'Close' : 'Cancel'}
        </Button>
        {!queryResults && (
          <Button
            onClick={handleQueryFlags}
            variant="contained"
            color="secondary"
            startIcon={<FlagIcon />}
            disabled={loading || selectedDevices.size === 0}
          >
            Query Flags ({selectedDevices.size})
          </Button>
        )}
        {queryResults && (
          <Button
            onClick={() => {
              setQueryResults(null);
              setSuccess(false);
            }}
            variant="outlined"
            color="secondary"
          >
            Query More
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForensicFlagsDialog;

